import { ClarionButton } from '@clarion/ClarionButton';
import { ClarionTag } from '@clarion/ClarionTag';
import ClarionPopover from '@clarion/ClarionPopover';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bell, ShieldAlert, X } from 'lucide-react';
import TimeAgo from 'react-timeago';
import { useWorkspaces } from '../auth/WorkspaceState';
import { useSideNavigation } from '../components/navigation/SideNavigationContext';
import { cx } from '../util';
import {
  type AppNotification,
  getNotifications,
  markNotificationRead,
} from './notificationApiClient';

const POLL_INTERVAL_MS = 300_000;

export function NotificationBell() {
  const { current, workspaces } = useWorkspaces();
  const workspaceId = current?.workspaceId;
  const workspaceIds = workspaces?.map((ws) => ws.workspace.workspaceId) ?? [];
  const queryClient = useQueryClient();
  const { isCollapsed } = useSideNavigation();

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications', workspaceIds.join(',')],
    queryFn: () => getNotifications(workspaceId!, workspaceIds),
    enabled: Boolean(workspaceId) && workspaceIds.length > 0,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 60_000,
  });

  const dismissMutation = useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(workspaceId!, notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({
        queryKey: ['notifications'],
      });
      queryClient.setQueryData<AppNotification[]>(
        ['notifications', workspaceIds.join(',')],
        (old) => (old ?? []).filter((n) => n.id !== notificationId),
      );
    },
  });

  const unreadCount = notifications.length;
  const unreadLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <ClarionPopover
      title="Notifications"
      target={
        <ClarionButton
          variant="minimal"
          size="large"
          icon={Bell}
          fill
          textAlign="left"
          tooltip={isCollapsed ? 'Notifications' : undefined}
          tooltipPlacement="right"
          aria-label={`Notifications, ${unreadCount} unread`}
          className={cx('relative ', isCollapsed && 'max-w-10 px-3')}
        >
          <div className="flex justify-between items-center gap-2 w-full min-w-0">
            {!isCollapsed && 'Notifications'}
            {unreadCount > 0 && (
              <ClarionTag
                intent="danger"
                className={cx(isCollapsed && 'absolute top-0 -right-1 pointer-events-none')}
              >
                {unreadLabel}
              </ClarionTag>
            )}
          </div>
        </ClarionButton>
      }
    >
      <div className="w-80 max-h-96 overflow-y-auto -m-4 p-4">
        {notifications.length === 0 ? (
          <p className="text-sm opacity-75 text-center py-4">No new notifications</p>
        ) : (
          <ul className="space-y-3">
            {notifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onDismiss={() => dismissMutation.mutate(notification.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </ClarionPopover>
  );
}

function NotificationRow({
  notification,
  onDismiss,
}: {
  notification: AppNotification;
  onDismiss: () => void;
}) {
  const SeverityIcon = notification.severity === 'danger' ? ShieldAlert : AlertTriangle;
  const iconColor = notification.severity === 'danger' ? 'text-red-500' : 'text-amber-500';

  return (
    <li className="flex gap-3 items-start group">
      <SeverityIcon className={cx('w-5 h-5 mt-0.5 shrink-0', iconColor)} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-1">
          <p className="text-sm font-medium leading-snug">{notification.title}</p>
          <ClarionButton
            tooltip="Dismiss notification"
            size="small"
            icon={X}
            onPress={onDismiss}
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 shrink-0 -mt-1 -mr-1"
          />
        </div>
        <p className="text-sm opacity-90 mt-0.5 leading-snug">{notification.message}</p>
        <div className="flex items-center gap-2 mt-1">
          <ClarionTag minimal intent={notification.severity === 'danger' ? 'danger' : 'warning'}>
            {notification.type === 'budget_cap_reached' ? 'Cap reached' : 'Budget alert'}
          </ClarionTag>
          <span className="text-xs opacity-80">
            <TimeAgo date={notification.createdAt} />
          </span>
        </div>
      </div>
    </li>
  );
}
